import { useCallback, useEffect, useMemo } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { serverErrorMessage } from "../lib/apiError";
import { getCsrfHeader } from "../lib/csrf";
import { type CachedQueryStatus, useCachedQuery } from "./useCachedQuery";

// Mirrors SupportMessageSummary.kt / SupportMessage.kt. Written by the gateway's
// ZTC_SEND_SUPPORT_MESSAGE tool; the list omits bodies, the side panel loads one.
export type SupportMessageSummary = {
  id: string;
  tenantId: string;
  tenantName: string | null;
  agentName: string | null;
  kind: string;
  subject: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
};

export type SupportMessage = SupportMessageSummary & { body: string };

// `before` is the id of the last row on the previous page.
export type SupportMessagesFilter = { tenantId: string | null; unreviewedOnly: boolean; before: string | null };

// The server's default page size, so a full page means there may be older rows.
export const SUPPORT_MESSAGES_PAGE_SIZE = 200;

export type SupportMessageResult = { ok: true; value: SupportMessage } | { ok: false; message: string };

export type UseAdminSupportMessagesResult = {
  status: CachedQueryStatus;
  messages: ReadonlyArray<SupportMessageSummary>;
  error: string | null;
  isFetching: boolean;
  markReviewed: (id: string) => Promise<SupportMessageResult>;
};

export type UseSupportMessageResult = {
  status: CachedQueryStatus;
  message: SupportMessage | null;
  error: string | null;
  replace: (message: SupportMessage) => void;
};

const LIST_URL = ADMIN_QUERIES.supportMessages.url;
const UNREVIEWED_URL = `${LIST_URL}?unreviewed=true&limit=100`;
// Tells the sidebar badge, which runs its own query, that a review landed.
const REVIEWED_EVENT = "ztc:support-reviewed";

export const supportMessagesUrl = (filter: SupportMessagesFilter): string => {
  const params = new URLSearchParams();
  if (filter.tenantId !== null) params.set("tenantId", filter.tenantId);
  if (filter.unreviewedOnly) params.set("unreviewed", "true");
  if (filter.before !== null) params.set("before", filter.before);
  const query = params.toString();
  return query === "" ? LIST_URL : `${LIST_URL}?${query}`;
};

export const supportMessageUrl = (id: string): string => `${LIST_URL}/${encodeURIComponent(id)}`;

const getJson =
  <T,>(url: string, failure: string) =>
  async (signal: AbortSignal): Promise<T> => {
    const res = await fetch(url, { credentials: "include", signal });
    if (!res.ok) throw new Error(`${failure} (${res.status})`);
    return (await res.json()) as T;
  };

export const useAdminSupportMessages = (filter: SupportMessagesFilter): UseAdminSupportMessagesResult => {
  const url = supportMessagesUrl(filter);
  const fetcher = useMemo(
    () => getJson<ReadonlyArray<SupportMessageSummary>>(url, "Failed to load support messages"),
    [url],
  );
  const { status, data, error, isFetching, refetch } = useCachedQuery<ReadonlyArray<SupportMessageSummary>>({
    cacheResource: url === LIST_URL ? ADMIN_QUERIES.supportMessages.resource : null,
    queryKey: url,
    fetcher,
  });

  const markReviewed = useCallback(
    async (id: string): Promise<SupportMessageResult> => {
      try {
        const res = await fetch(`${supportMessageUrl(id)}/review`, {
          method: "POST",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (!res.ok) return { ok: false, message: await serverErrorMessage(res, "Could not mark it reviewed") };
        const updated = (await res.json()) as SupportMessage;
        // Refetch rather than patch: the filter may change while either request is in flight.
        void refetch();
        window.dispatchEvent(new Event(REVIEWED_EVENT));
        return { ok: true, value: updated };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [refetch],
  );

  return { status, messages: data ?? [], error, isFetching, markReviewed };
};

// One message with its body, for the side panel. Polled like the list so a review by
// another admin shows up even when the row has left a filtered list.
export const useSupportMessage = (id: string): UseSupportMessageResult => {
  const url = supportMessageUrl(id);
  const fetcher = useMemo(() => getJson<SupportMessage>(url, "Failed to load the message"), [url]);
  const { status, data, error, mutate } = useCachedQuery<SupportMessage>({
    cacheResource: null,
    queryKey: url,
    fetcher,
  });
  const replace = useCallback((message: SupportMessage): void => mutate(() => message), [mutate]);
  return { status, message: data, error, replace };
};

export const useUnreviewedSupportCount = (): number => {
  const fetcher = useMemo(
    () => getJson<ReadonlyArray<SupportMessageSummary>>(UNREVIEWED_URL, "Failed to load support messages"),
    [],
  );
  const { data, refetch } = useCachedQuery<ReadonlyArray<SupportMessageSummary>>({
    cacheResource: null,
    queryKey: UNREVIEWED_URL,
    fetcher,
  });
  useEffect(() => {
    const onReviewed = (): void => void refetch();
    window.addEventListener(REVIEWED_EVENT, onReviewed);
    return () => window.removeEventListener(REVIEWED_EVENT, onReviewed);
  }, [refetch]);
  return data?.length ?? 0;
};
